/* /tools/dinner-plate — host landing (My events list + New event CTA) */
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Utensils, Users, Calendar, Loader2, ExternalLink } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { dp } from "../lib/dinnerPlateApi";
import { toast } from "sonner";

export default function DinnerPlatePage() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [events, setEvents] = useState(null);

  useEffect(() => { if (!loading && !user) nav("/login", { replace: true }); }, [user, loading, nav]);
  useEffect(() => {
    if (user) dp.listEvents().then(setEvents).catch(() => { toast.error("Couldn't load events"); setEvents([]); });
  }, [user]);

  if (loading || events === null) {
    return <div className="py-12 flex justify-center"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  }

  return (
    <div className="animate-fade-up" data-testid="dinner-plate-page">
      <div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
        <div>
          <span className="label-caps">Group dining</span>
          <h1 className="mt-1 text-3xl sm:text-4xl font-display font-black tracking-tighter">Dinner Plate</h1>
          <p className="text-sm mt-1" style={{ color: "var(--text-muted)" }}>
            Snap a restaurant menu, share a link, let everyone pre-order before you arrive.
          </p>
        </div>
        <Link
          to="/tools/dinner-plate/new"
          data-testid="dp-new-event"
          className="inline-flex items-center gap-2 h-10 px-4 rounded-lg text-sm font-semibold text-white"
          style={{ background: "var(--primary)" }}
        >
          <Plus className="h-4 w-4" /> New dinner
        </Link>
      </div>

      {events.length === 0 ? (
        <div className="card-tactile rounded-2xl p-10 text-center" data-testid="dp-empty">
          <Utensils className="h-10 w-10 mx-auto mb-3" style={{ color: "var(--primary-ink)" }} />
          <h2 className="font-display tracking-tight text-xl mb-1">No dinners yet</h2>
          <p className="text-sm" style={{ color: "var(--text-muted)" }}>
            Upload a menu, share the link, gather orders — all before the food arrives.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {events.map(evt => (
            <Link key={evt.id} to={`/tools/dinner-plate/events/${evt.id}`}
                  data-testid={`dp-event-${evt.id}`}
                  className="card-tactile rounded-2xl p-5 group">
              <div className="flex items-start justify-between gap-3 mb-2">
                <h3 className="font-display tracking-tight text-lg" style={{ color: "var(--primary-ink)" }}>{evt.title}</h3>
                <ExternalLink className="h-4 w-4 shrink-0" style={{ color: "var(--text-muted)" }} />
              </div>
              <div className="text-xs flex items-center gap-3 flex-wrap" style={{ color: "var(--text-muted)" }}>
                {evt.restaurant_name && <span>· {evt.restaurant_name}</span>}
                {evt.event_date && <span><Calendar className="h-3 w-3 inline mr-1" />{new Date(evt.event_date).toLocaleString()}</span>}
                <span><Users className="h-3 w-3 inline mr-1" />{evt.order_count} order(s)</span>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
